import React, { createContext, useContext, useState, useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';
import { translations, SUPPORTED_LANGUAGES } from './translations';

const LanguageContext = createContext();

export function detectDefaultLanguage() {
  const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (browserLang.startsWith('ja')) {
    return 'ja';
  }
  if (
    browserLang.includes('tw') ||
    browserLang.includes('hk') ||
    browserLang.includes('mo') ||
    browserLang.includes('hant')
  ) {
    return 'zh-TW';
  }
  if (browserLang.startsWith('zh')) {
    return 'zh-CN';
  }
  return 'en';
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem('app_language');
      if (stored && ['en', 'ja', 'zh-TW', 'zh-CN'].includes(stored)) {
        return stored;
      }
    } catch (e) {
      // Ignore
    }
    return detectDefaultLanguage();
  });

  useEffect(() => {
    async function loadSavedLang() {
      try {
        const item = await Preferences.get({ key: 'app_language' });
        if (item && item.value && ['en', 'ja', 'zh-TW', 'zh-CN'].includes(item.value)) {
          setLanguageState(item.value);
        }
      } catch (e) {
        // Fallback to localStorage or default
      }
    }
    loadSavedLang();
  }, []);

  const setLanguage = async (newLang) => {
    if (!['en', 'ja', 'zh-TW', 'zh-CN'].includes(newLang)) return;
    setLanguageState(newLang);
    try {
      localStorage.setItem('app_language', newLang);
      await Preferences.set({ key: 'app_language', value: newLang });
    } catch (e) {
      // Ignore
    }
  };

  const getNestedValue = (obj, keyPath) => {
    if (!obj || !keyPath) return null;
    const parts = keyPath.split('.');
    let cur = obj;
    for (const part of parts) {
      if (cur == null || typeof cur !== 'object') return null;
      cur = cur[part];
    }
    return cur;
  };

  const t = (key, params = {}, fallback = null) => {
    if (typeof params === 'string') {
      fallback = params;
      params = {};
    }

    let value = getNestedValue(translations[language], key);
    if (value == null) {
      value = getNestedValue(translations['en'], key);
    }
    if (value == null) {
      return fallback != null ? fallback : key;
    }

    if (typeof value === 'string' && params && typeof params === 'object') {
      Object.keys(params).forEach((paramKey) => {
        value = value.replace(new RegExp(`{{${paramKey}}}`, 'g'), params[paramKey]);
      });
    }

    return value;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        languages: SUPPORTED_LANGUAGES
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: 'en',
      setLanguage: () => {},
      t: (k, params, fallback) => (typeof params === 'string' ? params : fallback || k),
      languages: SUPPORTED_LANGUAGES
    };
  }
  return context;
}

export const useTranslation = useLanguage;
export default LanguageContext;
